/**
 * Our `FieldSpec` → Puck's `Field`.
 *
 * This translation is the whole reason `packages/blocks` never imports `@puckeditor/core`.
 * It costs one function and buys two things: block specs that run in plain Node (so the
 * export pipeline is testable headlessly), and a single blast radius if Puck — which is
 * pre-1.0 — changes its field API.
 */

import type { Field } from '@puckeditor/core';
import type { FieldSpec } from '@lp/shared';
import { CtaField } from '../fields/CtaField.js';
import { IconField } from '../fields/IconField.js';
import { ImageField } from '../fields/ImageField.js';
import { LinkField } from '../fields/LinkField.js';
import { PaddingField } from '../fields/PaddingField.js';

/**
 * Puck's `BaseField` has no `help` slot, so the hint is appended to the label.
 *
 * Not ideal, but better than a parallel tooltip system — and these hints carry real rules
 * ("aim for 55 characters or fewer"), so they need to be where the author is looking.
 */
function labelFor(spec: FieldSpec): string {
  const help = 'help' in spec && spec.help ? ` — ${spec.help}` : '';
  return `${spec.label}${help}`;
}

export function fieldSpecToPuck(spec: FieldSpec): Field {
  const label = labelFor(spec);

  switch (spec.type) {
    case 'text':
      return { type: 'text', label };

    case 'textarea':
      return { type: 'textarea', label };

    case 'number':
      return { type: 'number', label, ...(spec.min !== undefined ? { min: spec.min } : {}), ...(spec.max !== undefined ? { max: spec.max } : {}) } as Field;

    case 'select':
      return { type: 'select', label, options: spec.options };

    case 'radio':
      return { type: 'radio', label, options: spec.options };

    case 'boolean':
      return {
        type: 'radio',
        label,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      };

    case 'date':
      // Rendered as a native date input by DateField, which is what guarantees ISO 8601 —
      // the live site currently emits "Sep 11, 2026", which Google does not parse.
      return { type: 'text', label: `${label} (YYYY-MM-DD)` };

    // Puck calls it "richtext", lowercase t.
    case 'richText':
      return { type: 'richtext', label };

    case 'slot':
      // NB: SlotField has no min/max. Cardinality lives in the block's validate().
      return { type: 'slot', label, allow: spec.allow };

    case 'object':
      return {
        type: 'object',
        label,
        objectFields: Object.fromEntries(
          Object.entries(spec.fields).map(([k, v]) => [k, fieldSpecToPuck(v)]),
        ),
      } as Field;

    case 'array':
      return {
        type: 'array',
        label,
        arrayFields: Object.fromEntries(
          Object.entries(spec.of).map(([k, v]) => [k, fieldSpecToPuck(v)]),
        ),
        ...(spec.summaryField ? { getItemSummary: (item: Record<string, unknown>) => String(item[spec.summaryField!] ?? '') } : {}),
      } as Field;

    case 'cta':
      return { type: 'custom', label, render: CtaField };

    case 'padding':
      return { type: 'custom', label, render: PaddingField };

    case 'icon':
      return { type: 'custom', label, render: IconField };

    case 'image':
      return { type: 'custom', label, render: (props) => ImageField({ ...props, role: spec.role }) };

    case 'link':
      return { type: 'custom', label, render: (props) => LinkField({ ...props, internalOnly: spec.internalOnly }) };

    case 'author':
      // Until the author roster lands, a plain slug field. Resolves to /blog-authors/<slug>.
      return { type: 'text', label: `${label} (slug)` };
  }
}
